import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertTriangle,
  ArrowLeft,
  Download,
  Lock,
  MessageSquarePlus,
  ShieldAlert,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const HOT_POTATO_LABELS = {
  facilities: "Facilities",
  security: "Security",
  budget: "Budget",
  decision: "Decision",
  staff_volunteer: "Staff / Volunteer",
};
const HOT_POTATO_ORDER = ["facilities", "security", "budget", "decision", "staff_volunteer"];
const PULSE_TONE = {
  thriving: "bg-[#3F7AD8] text-cream-100",
  healthy: "bg-forest-600 text-cream-100",
  struggles: "bg-[#D19A2E] text-cream-100",
  issues: "bg-terracotta-500 text-cream-100",
  stretched: "bg-[#D19A2E] text-cream-100",
  depleted: "bg-terracotta-500 text-cream-100",
};
const STATUS_LABEL = {
  done: "Done",
  in_progress: "In progress",
  dropped: "Dropped",
};

function PulseBadge({ value }) {
  if (!value) return <span className="text-xs italic text-charcoal-muted">—</span>;
  const tone = PULSE_TONE[value] || "bg-sage-300 text-charcoal";
  return (
    <span className={`inline-flex rounded-full px-3 py-0.5 text-xs font-medium capitalize ${tone}`}>
      {value}
    </span>
  );
}

export default function WeeklyWinsReport() {
  const { reportId } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addingNote, setAddingNote] = useState(false);
  const [note, setNote] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/weekly-wins/reports/${reportId}`);
      setReport(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Couldn't load this report.");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [reportId]);

  const addNote = async () => {
    const v = note.trim();
    if (!v) return;
    try {
      const { data } = await api.post(`/weekly-wins/reports/${reportId}/note`, { note: v });
      setReport(data);
      setNote(""); setAddingNote(false);
      toast.success("Note added.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/weekly-wins/reports/${reportId}/pdf`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `weekly-wins-${report?.campus_name || "report"}-${report?.week_start || ""}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Couldn't download the PDF.");
    }
  };

  if (loading) {
    return (
      <div className="patria-card p-8" data-testid="ww-report-loading">
        <p className="italic text-charcoal-muted">Loading report…</p>
      </div>
    );
  }
  if (!report?.id) {
    return (
      <div className="patria-card p-8" data-testid="ww-report-empty">
        <p className="italic text-charcoal-muted">Report not found.</p>
      </div>
    );
  }

  const flagged = HOT_POTATO_ORDER.filter((k) => report.hot_potatoes?.[k]?.flag);

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="weekly-wins-report">
      <div>
        <Link to="/rhythm/weekly-wins/rollup" className="text-sm text-forest-700 inline-flex items-center gap-1">
          <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Back To Roll-Up
        </Link>
        <div className="flex items-start justify-between gap-3 mt-2 flex-wrap">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-terracotta-500 font-medium">
              Weekly Wins · Campus Health Briefing
            </p>
            <h1 className="text-4xl font-serif text-forest-900">{report.campus_name}</h1>
            <p className="text-sm text-charcoal-soft mt-1">
              Week of {report.week_start} → {report.week_end}
              {report.is_late && (
                <span className="ml-2 patria-chip-terra text-[10px]" data-testid="report-late-chip">LATE</span>
              )}
            </p>
            <p className="text-xs text-charcoal-muted mt-1">
              Submitted by {report.submitted_by_name || "—"} ·{" "}
              {(report.submitted_at || "").slice(0, 19).replace("T", " ")}
            </p>
            {(report.editor_names || []).length > 1 && (
              <p className="text-[11px] text-charcoal-muted">
                Editors: {report.editor_names.join(", ")}
              </p>
            )}
          </div>
          <div className="flex flex-col items-end gap-1">
            <span className="patria-chip-green" data-testid="report-locked-chip">
              <Lock className="h-3 w-3 mr-1" strokeWidth={2} /> Submitted · Locked
            </span>
            <button type="button" onClick={downloadPdf} className="patria-btn-ghost text-xs" data-testid="btn-report-pdf">
              <Download className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} /> PDF
            </button>
          </div>
        </div>
      </div>

      <section className="patria-card p-6" data-testid="report-section-service">
        <p className="text-[10px] uppercase tracking-widest text-terracotta-500 font-medium">Section 1</p>
        <h2 className="font-serif text-2xl text-forest-900 mt-1">Service(s) Pulse</h2>
        <div className="mt-3"><PulseBadge value={report.service_pulse} /></div>
        <p className="mt-3 text-sm text-charcoal whitespace-pre-wrap" data-testid="report-service-narrative">
          {report.service_pulse_narrative || <span className="italic text-charcoal-muted">(no narrative)</span>}
        </p>
      </section>

      <section className="patria-card p-6 border-l-4 border-forest-600" data-testid="report-section-leader">
        <div className="flex items-center gap-2">
          <ShieldAlert className="h-4 w-4 text-forest-700" strokeWidth={1.5} />
          <p className="text-[10px] uppercase tracking-widest text-forest-700 font-medium">
            Section 2 · Private (Submitter, Reviewer, People Operations)
          </p>
        </div>
        <h2 className="font-serif text-2xl text-forest-900 mt-1">Leader Pulse</h2>
        <div className="mt-3"><PulseBadge value={report.leader_pulse} /></div>
        <p className="mt-3 text-sm text-charcoal whitespace-pre-wrap" data-testid="report-leader-narrative">
          {report.leader_pulse_narrative || <span className="italic text-charcoal-muted">(no narrative)</span>}
        </p>
      </section>

      <section className="patria-card p-6" data-testid="report-section-hp">
        <p className="text-[10px] uppercase tracking-widest text-terracotta-500 font-medium">Section 3</p>
        <h2 className="font-serif text-2xl text-forest-900 mt-1">Hot Potatoes</h2>
        {flagged.length === 0 ? (
          <p className="mt-2 text-sm italic text-charcoal-muted">No hot potatoes this week.</p>
        ) : (
          <ul className="mt-3 space-y-3">
            {flagged.map((k) => (
              <li key={k} className="p-3 rounded-xl bg-terracotta-500/10" data-testid={`report-hp-${k}`}>
                <p className="font-medium text-forest-900 inline-flex items-center gap-1">
                  <AlertTriangle className="h-4 w-4 text-terracotta-500" strokeWidth={1.5} />
                  {HOT_POTATO_LABELS[k]}
                </p>
                <p className="text-sm text-charcoal mt-1 whitespace-pre-wrap">
                  {report.hot_potatoes[k].description || <span className="italic text-charcoal-muted">(no description)</span>}
                </p>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="patria-card p-6" data-testid="report-section-commitments">
        <p className="text-[10px] uppercase tracking-widest text-terracotta-500 font-medium">Section 4</p>
        <h2 className="font-serif text-2xl text-forest-900 mt-1">Weekly Wins Commitments</h2>
        <ul className="mt-3 space-y-3">
          {(report.commitments || []).filter((c) => (c.text || "").trim()).map((c, i) => (
            <li key={i} className="p-3 rounded-xl bg-sage-200/40" data-testid={`report-commitment-${i}`}>
              <p className="text-forest-900 text-sm">&ldquo;{c.text}&rdquo;</p>
              {c.prior_week_status && (
                <p className="text-[11px] mt-1 text-terracotta-700 italic">
                  Prior-week status: {STATUS_LABEL[c.prior_week_status] || c.prior_week_status}
                </p>
              )}
            </li>
          ))}
          {(report.commitments || []).every((c) => !(c.text || "").trim()) && (
            <li className="text-xs italic text-charcoal-muted">No commitments recorded.</li>
          )}
        </ul>
      </section>

      <section className="patria-card p-6" data-testid="report-section-notes">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <h2 className="font-serif text-2xl text-forest-900">Post-Submission Notes</h2>
          {!addingNote && (
            <button
              type="button"
              className="patria-btn-secondary text-sm"
              onClick={() => setAddingNote(true)}
              data-testid="btn-report-add-note"
            >
              <MessageSquarePlus className="h-4 w-4 mr-1" strokeWidth={1.5} /> Add Note
            </button>
          )}
        </div>
        <ul className="mt-3 space-y-3">
          {(report.post_submission_notes || []).map((n, i) => (
            <li key={i} className="p-3 rounded-xl bg-sage-200/40" data-testid={`report-note-${i}`}>
              <p className="text-xs text-charcoal-muted">
                {n.by_user_name} · {(n.at || "").slice(0, 19).replace("T", " ")}
              </p>
              <p className="text-forest-900 text-sm mt-1 whitespace-pre-wrap">{n.note}</p>
            </li>
          ))}
          {(report.post_submission_notes || []).length === 0 && (
            <li className="text-xs italic text-charcoal-muted">No notes yet.</li>
          )}
        </ul>
        {addingNote && (
          <div className="mt-3 space-y-2">
            <textarea
              className="patria-input w-full min-h-[100px]"
              placeholder="Add a note after the fact…"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid="report-note-textarea"
            />
            <div className="flex gap-2">
              <button
                type="button"
                className="patria-btn-primary text-sm"
                onClick={addNote}
                disabled={!note.trim()}
                data-testid="btn-report-save-note"
              >
                Save Note
              </button>
              <button
                type="button"
                className="patria-btn-ghost text-sm"
                onClick={() => { setAddingNote(false); setNote(""); }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
